import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCategoryGradient, getBlogHeroImage } from '@/lib/blogCategoryStyles';

const VARIANT_CONFIG = {
  home: {
    heroHeight: 'h-48',
    pillPosition: 'top-3 right-3',
    pillShape: 'rounded',
    pillIcon: false,
    bodyPadding: 'p-6',
    showTags: false,
    ctaLabel: 'Read More',
  },
  archive: {
    heroHeight: 'h-40 md:h-48',
    pillPosition: 'top-3 left-3 md:top-4 md:left-4',
    pillShape: 'rounded-full',
    pillIcon: false,
    bodyPadding: 'p-4 md:p-6',
    showTags: true,
    ctaLabel: 'Read Full Article',
  },
  related: {
    heroHeight: 'h-48',
    pillPosition: 'top-3 left-3',
    pillShape: 'rounded-full',
    pillIcon: true,
    bodyPadding: 'p-6',
    showTags: false,
    ctaLabel: 'Read More',
  },
};

const BlogCard = ({ post, variant = 'home', index = 0, className = '' }) => {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.home;
  const href = `/blog/${post.slug}`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className={`bg-white rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-[#422680] hover:shadow-xl transition-all group flex flex-col h-full ${className}`}
    >
      <Link
        to={href}
        aria-label={`Read more about ${post.title}`}
        className={`relative ${config.heroHeight} overflow-hidden block bg-gradient-to-br ${getCategoryGradient(post.category)}`}
      >
        <img
          src={getBlogHeroImage(post)}
          alt=""
          loading="lazy"
          width={1200}
          height={630}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div
          className={`absolute ${config.pillPosition} inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-[#422680] text-[10px] md:text-xs font-bold px-2 py-1 md:px-3 md:py-1 ${config.pillShape} shadow-sm z-20`}
        >
          {config.pillIcon && <Shield size={12} />}
          {post.category}
        </div>
      </Link>

      <div className={`${config.bodyPadding} flex-1 flex flex-col`}>
        <div className="flex items-center gap-4 text-xs md:text-sm text-gray-500 mb-3">
          {post.date && (
            <div className="flex items-center gap-1">
              <Calendar size={14} />
              <span>{post.date}</span>
            </div>
          )}
          {post.readTime && (
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>{post.readTime}</span>
            </div>
          )}
        </div>

        <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3 flex-1">
          {post.excerpt}
        </p>

        {config.showTags && Array.isArray(post.tags) && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.slice(0, 3).map((tag) => (
              <Link
                key={tag}
                to={`/blog?tag=${tag}`}
                className="bg-gray-50 hover:bg-[#422680] hover:text-white text-gray-600 text-[10px] md:text-xs px-2 py-1 rounded border border-gray-100 transition-colors"
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}

        <Link to={href} className="mt-auto" aria-label={`${config.ctaLabel}: ${post.title}`}>
          <Button
            variant="link"
            className="text-[#422680] hover:text-[#F54952] p-0 group/btn w-full justify-start text-sm md:text-base"
          >
            {config.ctaLabel}
            <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" size={16} />
          </Button>
        </Link>
      </div>
    </motion.article>
  );
};

export default BlogCard;
